import React, { Dispatch, SetStateAction } from "react"
import { useDeviceType } from "utils/MediaQuery"
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import { Colors, PLANLIST } from "Const"
import { PAGE } from "pages/Plans/Plans"
import { EasyCalcResIf } from "utils/ifs/EasyCalcRes"
import { useSelector } from "react-redux"
import { AppState } from "store/store"
import { loadResponseEasyCalc } from "utils/JsonManipulation"
import styles from "./styles.module.scss"

/**
 * Step4見積結果画面の見積もり金額表示カードの部品郡
 *
 */
const formatNumber = (num: string | number) => {
  const numString = typeof num === "number" ? num.toString() : num
  return numString.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

interface OneTimeOrInstallmentProps {
  isAnnual: boolean
  planColor: string
  setIsAnnual: Dispatch<SetStateAction<boolean>>
}
const OneTimeOrInstallment = ({ isAnnual, planColor, setIsAnnual }: OneTimeOrInstallmentProps) => {
  const borderColor = `${planColor}_border`
  return (
    <div className={`${styles.payment}`}>
      <button
        type="button"
        className={`${styles.payment__oneTime} ${isAnnual ? styles.on : ""} ${isAnnual ? styles[planColor] : ""} ${
          styles[borderColor]
        }`}
        onClick={() => {
          setIsAnnual(true)
        }}
      >
        一括払保険料
      </button>
      <button
        type="button"
        className={`${styles.payment__installment} ${isAnnual ? "" : styles.on} ${isAnnual ? "" : styles[planColor]} ${
          styles[borderColor]
        }`}
        onClick={() => {
          setIsAnnual(false)
        }}
      >
        分割払保険料
      </button>
    </div>
  )
}
interface CardProps {
  page: number
  plan: string
  title: string
  scrollX: (goRIght: boolean) => void
  isAnnual: boolean
  setIsAnnual: Dispatch<SetStateAction<boolean>>
}
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ page, plan, title, scrollX, isAnnual, setIsAnnual }, ref) => {
    const res: EasyCalcResIf = useSelector((state: AppState) => state.plan.plan)
    const data = loadResponseEasyCalc(res)
    const { isSmartPhone, isTablet, isMobile, isPc } = useDeviceType()
    let responseData: any
    if (plan === PLANLIST.LIGHT) {
      responseData = data.light
    } else if (plan === PLANLIST.NORMAL) {
      responseData = data.standard
    } else {
      responseData = data.fullSupport
    }
    const getPlanColor = () => {
      let color = ""
      if (plan === PLANLIST.LIGHT) {
        color = "green"
      } else if (plan === PLANLIST.NORMAL) {
        color = "blue"
      } else {
        color = "beige"
      }
      return color
    }
    const getBorderColor = () => {
      return styles[`card--${plan}`]
    }
    return (
      <div className={`${styles.card} ${getBorderColor()} ${styles.borderOrange}`}>
        {isSmartPhone && (
          <div
            role="button"
            tabIndex={0}
            className={styles.card__leftArrow}
            style={{ opacity: page === PAGE.LIGHT ? 0 : 1 }}
            onClick={() => {
              scrollX(false)
            }}
            onKeyDown={() => {}}
          >
            <div>
              <ArrowBackIosIcon style={{ color: Colors.White }} />
            </div>
          </div>
        )}
        <div className={styles.card__body}>
          <div className={styles.card__title}>{title}</div>
          <div className={styles.card__priceContainer}>
            <div className={styles.card__priceContainer__annual} style={{ display: isAnnual ? "" : "none" }}>
              {formatNumber(responseData.pricing.oneTime.toString())}
              <span>円</span>
              <span>（年間）</span>
            </div>
            <div className={styles.card__priceContainer__monthly} style={{ display: isAnnual ? "none" : "" }}>
              {formatNumber(responseData.pricing.firstInstallments.toString())}
              <span>円</span>
              <span>／月</span>
            </div>
          </div>
          <div className={styles.card__tagLine} style={{ opacity: isAnnual ? 0 : 1 }}>
            分割払保険料総額： {formatNumber(responseData.pricing.totalInstallments.toString())}円
          </div>
          <OneTimeOrInstallment isAnnual={isAnnual} planColor={getPlanColor()} setIsAnnual={setIsAnnual} />
          <div id="cardTransitionPoint" className={styles.card__internet_discount} ref={ref}>
            ネット割適用後
          </div>
        </div>
        {isSmartPhone && (
          <div
            role="button"
            tabIndex={0}
            className={styles.card__rightArrow}
            style={{ opacity: page === PAGE.FULLSUPPORT ? 0 : 1 }}
            onClick={() => {
              scrollX(true)
            }}
            onKeyDown={() => {}}
          >
            <div>
              <ArrowForwardIosIcon style={{ color: Colors.White }} />
            </div>
          </div>
        )}
      </div>
    )
  }
)

interface PlanCardProps {
  currentPage: number
  plan: string
  title: string
  scrollX: (goRIght: boolean) => void
  isAnnual: boolean
  setIsAnnual: Dispatch<SetStateAction<boolean>>
}
/**
 * Step4見積結果画面の見積もり金額表示カード
 * @param {number} currentPage - 画面で表示しているプラン
 * @param {string} plan - カード上に表示するプラン
 * @param {string} title - タイトル
 * @param {(boolean)=> void} scrollX - 左右のスクロール
 * @param {number} isAnnual - 一括払保険料フラグ
 * @param {Dispatch<SetStateAction<boolean>>} setIsAnnual - 一括払保険料フラグのスイッチ
 */
const PlanCard = React.forwardRef<HTMLDivElement, PlanCardProps>(
  ({ currentPage, plan, title, scrollX, isAnnual, setIsAnnual }, ref) => {
    return (
      <div className={styles.root}>
        <div className={styles[`root__${plan}`]}>
          <Card
            page={currentPage}
            plan={plan}
            title={title}
            scrollX={scrollX}
            isAnnual={isAnnual}
            setIsAnnual={setIsAnnual}
            ref={ref}
          />
        </div>
      </div>
    )
  }
)

export default PlanCard
